import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import { setCheckingAuth } from '@/services/api/client';
import type { User, SystemStatus } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const systemStatus = ref<SystemStatus | null>(null);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.is_admin ?? false);
  const needsSetup = computed(() => false); // FusionAuth gère les utilisateurs
  const registrationEnabled = computed(() => false); // FusionAuth gère les inscriptions

  async function handleCallback(code: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await api.loginWithCallback(code);
      user.value = response.user;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Authentication failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchUser(): Promise<void> {
    if (!api.isAuthenticated()) return;

    loading.value = true;
    error.value = null;
    try {
      user.value = await api.getMe();
    } catch (err) {
      user.value = null;
      error.value = err instanceof Error ? err.message : 'Failed to fetch user';
    } finally {
      loading.value = false;
    }
  }

  function logout(): void {
    api.logout();
    user.value = null;
  }

  async function checkAuth(): Promise<boolean> {
    if (!api.isAuthenticated()) {
      user.value = null;
      return false;
    }

    setCheckingAuth(true);
    try {
      await fetchUser();
      return !!user.value;
    } catch {
      user.value = null;
      return false;
    } finally {
      setCheckingAuth(false);
    }
  }

  async function fetchSystemStatus(): Promise<void> {
    try {
      systemStatus.value = await api.getSystemStatus();
    } catch {
      systemStatus.value = { needs_setup: false, registration_enabled: false };
    }
  }

  return {
    user,
    loading,
    error,
    systemStatus,
    isAuthenticated,
    isAdmin,
    needsSetup,
    registrationEnabled,
    handleCallback,
    fetchUser,
    logout,
    checkAuth,
    fetchSystemStatus,
  };
});
